import Select, {
  ActionMeta,
  MultiValue,
  SingleValue,
  StylesConfig
} from "react-select";
import { PropsWithChildren } from "react";

type OptionType = {
  value: string;
  label: string;
  isFixed?: boolean;
  isHidden?: boolean;
};

interface DropdownProps {
  value?: string;
  values?: OptionType[];
  options?: OptionType[];
  onChange?: (value: OptionType | MultiValue<OptionType> | null) => void;
  isSearchable?: boolean;
  isClearable?: boolean;
  isMulti?: boolean;
  isDisabled?: boolean;
  placeholder?: string;
  className?: string;
  fixedWidth?: boolean;
}

function Dropdown({
  value,
  values,
  onChange,
  options = [],
  isMulti = false,
  isSearchable = false,
  isClearable = false,
  isDisabled = false,
  fixedWidth = true,
  placeholder = "",
  className = ""
}: PropsWithChildren<DropdownProps>) {
  const onChangeFilterFixedOptions = (
    newSelection: SingleValue<OptionType> | MultiValue<OptionType>,
    actionMeta: ActionMeta<OptionType>
  ) => {
    if (!onChange) {
      return;
    }

    const previousSelection = value;

    switch (actionMeta.action) {
      case "remove-value":
      case "pop-value":
        if (Array.isArray(newSelection) && actionMeta.removedValue?.isFixed) {
          return previousSelection;
        }
        break;
      default:
        break;
    }

    onChange(newSelection);
  };

  const customStyles: StylesConfig<OptionType, boolean> = {
    container: (provided) => ({
      ...provided,
      boxShadow: "none",
      borderRadius: 0,
      outline: "none"
    }),
    multiValue: (base, state) => {
      const commonStyle = {
        margin: "0 1.5em 0 0",
        fontSize: "var(--input-font-size)",
        backgroundColor: "rgba(0, 0, 0, 0)",
        flexFlow: "row-reverse"
      };

      if (state.data.isHidden) {
        return {
          ...base,
          display: "none"
        };
      }

      return {
        ...base,
        ...commonStyle
      };
    },
    multiValueLabel: (base) => ({
      ...base,
      padding: "0",
      paddingLeft: "0",
      fontSize: "var(--input-font-size)",
      backgroundColor: "rgba(0, 0, 0, 0)"
    }),
    multiValueRemove: (base, state) =>
      state.data.isFixed
        ? {
            ...base,
            display: "none"
          }
        : {
            ...base,
            padding: "0 .25em 0 0",
            transform: "scale(1.75)",
            color: "var(--ludvig-red)",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0)"
            }
          },
    input: (base) => ({ ...base, padding: "0" }),
    control: (base, state) => ({
      ...base,
      outline: "none",
      border: state.isFocused
        ? "var(--input-border-focus)"
        : "var(--input-border)",
      boxShadow: "none",
      borderRadius: 0,
      minHeight: "unset",
      fontSize: "var(--input-font-size)",
      padding: "var(--input-padding)",
      "&:hover": {
        border: state.isFocused
          ? "var(--input-border-focus)"
          : "var(--input-border)"
      }
    }),
    option: (base, state) => {
      if (state.data.isHidden) {
        return {
          ...base,
          display: "none"
        };
      }

      return {
        ...base,
        background: state.isSelected ? "#bbb" : "#fff",
        borderBottom: "var(--input-border)",
        fontSize: "var(--input-font-size)"
      };
    },
    menu: (base) => ({
      ...base,
      boxShadow: "none",
      border: "var(--input-border)",
      margin: "0",
      borderRadius: "0",
      width: fixedWidth ? "100%" : "fit-content"
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: "0"
    }),
    indicatorSeparator: (base) => ({ ...base, display: "none" }),
    valueContainer: (base) => ({
      ...base,
      padding: "0",
      gap: ".5em"
    }),
    placeholder: (base) => ({
      ...base,
      padding: "0",
      color: "var(--input-placeholder-color)",
      margin: "0"
    }),
    singleValue: (base, state) => {
      const opacity = state.isDisabled ? 0.5 : 1;
      const transition = "opacity 300ms";
      return { ...base, opacity, transition };
    }
  };

  const valueMapped = options.find((o) => o.value === value);

  return (
    <Select
      noOptionsMessage={() => <>Inga möjliga val</>}
      isMulti={isMulti}
      styles={customStyles}
      className={className}
      placeholder={placeholder}
      options={options}
      isDisabled={isDisabled}
      isSearchable={isSearchable}
      isClearable={isClearable}
      onChange={onChangeFilterFixedOptions}
      value={isMulti ? values : valueMapped}
    />
  );
}

export default Dropdown;
